import { useSyncExternalStore } from 'react';
import type { Currency, DataKeyState, Locale, Provider, ProviderSettings, Settings } from '@/types';

export type DataSource = 'finnhub' | 'massive' | 'twelvedata';

const STORAGE_KEY = 'assetlist.settings';
const DEFAULT_SETTINGS: Settings = {
  llm: { activeProvider: 'claude', providers: {} },
  data: {},
  displayCurrency: 'USD',
  setupDone: false,
};

function load(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      ...DEFAULT_SETTINGS,
      ...parsed,
      llm: { ...DEFAULT_SETTINGS.llm, ...parsed.llm, providers: parsed.llm?.providers ?? {} },
      data: parsed.data ?? {},
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

let state: Settings = load();
const listeners = new Set<() => void>();

function commit(next: Settings) {
  state = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked (private mode / quota): keep the in-memory value for this session.
  }
  for (const l of listeners) l();
}

export const settingsStore = {
  get: (): Settings => state,
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  setActiveProvider(provider: Provider) {
    commit({ ...state, llm: { ...state.llm, activeProvider: provider } });
  },
  updateProvider(provider: Provider, patch: Partial<ProviderSettings>) {
    const current = state.llm.providers[provider] ?? { apiKey: '', model: '' };
    commit({ ...state, llm: { ...state.llm, providers: { ...state.llm.providers, [provider]: { ...current, ...patch } } } });
  },
  clearProvider(provider: Provider) {
    const { [provider]: _removed, ...rest } = state.llm.providers;
    commit({ ...state, llm: { ...state.llm, providers: rest } });
  },
  setDataKey(source: DataSource, patch: Partial<DataKeyState>) {
    const current = state.data[source] ?? { apiKey: '', status: 'unverified' as const };
    commit({ ...state, data: { ...state.data, [source]: { ...current, ...patch } } });
  },
  clearDataKey(source: DataSource) {
    const { [source]: _removed, ...rest } = state.data;
    commit({ ...state, data: rest });
  },
  setDisplayCurrency(currency: Currency) {
    commit({ ...state, displayCurrency: currency });
  },
  setSetupDone(done: boolean) {
    commit({ ...state, setupDone: done });
  },
  setLocale(locale: Locale | undefined) {
    commit({ ...state, locale });
  },
};

export function useSettings(): Settings {
  return useSyncExternalStore(settingsStore.subscribe, settingsStore.get);
}

export function activeLLM(s: Settings): ProviderSettings | undefined {
  return s.llm.providers[s.llm.activeProvider];
}
export function hasLLM(s: Settings): boolean {
  const p = activeLLM(s);
  return !!p && p.apiKey.trim().length > 0 && p.model.length > 0;
}
export function finnhubKey(s: Settings): string | undefined {
  const k = s.data.finnhub;
  return k && k.status !== 'invalid' && k.apiKey ? k.apiKey : undefined;
}
export function massiveKey(s: Settings): string | undefined {
  const k = s.data.massive;
  return k && k.status !== 'invalid' && k.apiKey ? k.apiKey : undefined;
}
export function twelvedataKey(s: Settings): string | undefined {
  const k = s.data.twelvedata;
  return k && k.status !== 'invalid' && k.apiKey ? k.apiKey : undefined;
}
/** A source for US quotes. */
export function hasQuoteSource(s: Settings): boolean {
  return !!finnhubKey(s) || !!massiveKey(s);
}
/** A keyed source for Canadian quotes: Twelve Data (Grow plan) or Finnhub (paid plans only; a free key is refused at request time). Without one, TMX Money (no key, delayed) prices them. */
export function hasCaQuoteSource(s: Settings): boolean {
  return !!twelvedataKey(s) || !!finnhubKey(s);
}
